import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Label } from 'app/components/ui/label';
import { Input } from 'app/components/ui/input';
import { Textarea } from 'app/components/ui/textarea';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'app/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'app/components/ui/select';
import { Switch } from 'app/components/ui/switch';
import { Separator } from 'app/components/ui/separator';
import { Button } from 'app/components/ui/button';
import { Badge } from 'app/components/ui/badge';
import {
  Settings,
  AlertCircle,
  Zap,
  Shield,
  Wrench,
  Database,
  Globe,
  Lock,
  Eye,
  EyeOff,
  RefreshCw,
} from 'lucide-react';

interface AdvancedSettingsProps {
  form: UseFormReturn<any>;
}

export default function AdvancedSettings({ form }: AdvancedSettingsProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  return (
    <>
      {/* Performance Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5" />
            Performance Settings
          </CardTitle>
          <CardDescription>
            Optimize your site's performance and caching behavior.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cache_level">Cache Level</Label>
              <Select
                value={form.watch('cache_level')}
                onValueChange={value => form.setValue('cache_level', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select cache level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">
                    <div className="flex items-center gap-2">
                      <span>Low</span>
                      <Badge variant="secondary">Faster updates</Badge>
                    </div>
                  </SelectItem>
                  <SelectItem value="medium">
                    <div className="flex items-center gap-2">
                      <span>Medium</span>
                      <Badge variant="secondary">Balanced</Badge>
                    </div>
                  </SelectItem>
                  <SelectItem value="high">
                    <div className="flex items-center gap-2">
                      <span>High</span>
                      <Badge variant="secondary">Better performance</Badge>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Higher cache levels improve performance but may show outdated
                content
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cache_ttl">Cache TTL (seconds)</Label>
              <Input
                id="cache_ttl"
                type="number"
                min="60"
                max="86400"
                {...form.register('cache_ttl', { valueAsNumber: true })}
                placeholder="3600"
              />
              <p className="text-xs text-muted-foreground">
                Time to live for cached content (1 hour = 3600 seconds)
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Switch
                id="debug_mode"
                checked={form.watch('debug_mode')}
                onCheckedChange={value => form.setValue('debug_mode', value)}
              />
              <Label htmlFor="debug_mode">Debug Mode</Label>
            </div>
            <p className="text-xs text-muted-foreground">
              Enable detailed error reporting and logging (disable in
              production)
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Security Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Security Settings
          </CardTitle>
          <CardDescription>
            Configure security headers and protection mechanisms.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Switch
                id="security_headers"
                checked={form.watch('security_headers')}
                onCheckedChange={value =>
                  form.setValue('security_headers', value)
                }
              />
              <Label htmlFor="security_headers">Enable Security Headers</Label>
            </div>
            <p className="text-xs text-muted-foreground">
              Add security headers like X-Frame-Options, X-Content-Type-Options
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Switch
                id="content_security_policy"
                checked={form.watch('content_security_policy')}
                onCheckedChange={value =>
                  form.setValue('content_security_policy', value)
                }
              />
              <Label htmlFor="content_security_policy">
                Enable Content Security Policy
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              Prevent XSS attacks by controlling resource loading
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Switch
                id="rate_limiting"
                checked={form.watch('rate_limiting')}
                onCheckedChange={value => form.setValue('rate_limiting', value)}
              />
              <Label htmlFor="rate_limiting">Enable Rate Limiting</Label>
            </div>
            <p className="text-xs text-muted-foreground">
              Prevent abuse by limiting requests per IP address
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="rate_limit_requests">
                Rate Limit (requests/min)
              </Label>
              <Input
                id="rate_limit_requests"
                type="number"
                min="10"
                max="1000"
                {...form.register('rate_limit_requests', {
                  valueAsNumber: true,
                })}
                placeholder="100"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="session_timeout">Session Timeout (minutes)</Label>
              <Input
                id="session_timeout"
                type="number"
                min="5"
                max="1440"
                {...form.register('session_timeout', { valueAsNumber: true })}
                placeholder="30"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Maintenance Mode */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wrench className="w-5 h-5" />
            Maintenance Mode
          </CardTitle>
          <CardDescription>
            Temporarily disable your site for maintenance or updates.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Switch
                id="maintenance_mode"
                checked={form.watch('maintenance_mode')}
                onCheckedChange={value =>
                  form.setValue('maintenance_mode', value)
                }
              />
              <Label htmlFor="maintenance_mode">Enable Maintenance Mode</Label>
            </div>
            <p className="text-xs text-muted-foreground">
              Show maintenance page to visitors while you update the site
            </p>
          </div>

          {form.watch('maintenance_mode') && (
            <div className="space-y-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="space-y-2">
                <Label htmlFor="maintenance_title">Maintenance Title</Label>
                <Input
                  id="maintenance_title"
                  {...form.register('maintenance_title')}
                  placeholder="Site Under Maintenance"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="maintenance_message">Maintenance Message</Label>
                <Textarea
                  id="maintenance_message"
                  {...form.register('maintenance_message')}
                  placeholder="We're currently performing maintenance to improve your experience. Please check back soon."
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="maintenance_eta">
                  Estimated Time (optional)
                </Label>
                <Input
                  id="maintenance_eta"
                  {...form.register('maintenance_eta')}
                  placeholder="2 hours"
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Advanced Database Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5" />
            Database Settings
          </CardTitle>
          <CardDescription>
            Advanced database configuration options.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="db_connection_limit">Connection Limit</Label>
              <Input
                id="db_connection_limit"
                type="number"
                min="5"
                max="100"
                {...form.register('db_connection_limit', {
                  valueAsNumber: true,
                })}
                placeholder="20"
              />
              <p className="text-xs text-muted-foreground">
                Maximum database connections
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="db_timeout">Query Timeout (seconds)</Label>
              <Input
                id="db_timeout"
                type="number"
                min="5"
                max="300"
                {...form.register('db_timeout', { valueAsNumber: true })}
                placeholder="30"
              />
              <p className="text-xs text-muted-foreground">
                Maximum query execution time
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Warning Banner */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-yellow-600" />
          <div>
            <p className="text-sm text-yellow-800 font-medium mb-1">
              ⚠️ Advanced Settings Warning
            </p>
            <p className="text-sm text-yellow-700 mb-0">
              These settings are for experienced users. Incorrect configuration
              may affect site functionality, performance, or security. Make sure
              to test changes in a development environment first.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
